import Paperdoll from '@/components/embeds/Paperdoll'
import Store, { StoreState } from '@/store'

import { AscendancyEmbedView } from '@/components/embeds/Ascendancy'
import { EquipmentStatPriority } from '@/components/embeds/EquipmentStatPriority'
import { PaperdollWithSets } from '@/components/embeds/PaperdollWithSets'
import { SkillRotationEmbed } from '@/components/embeds/SkillRoration'
import { MainStoreProvider } from '@maxroll-media-group/common-libs'
import { getTreePath, isDev } from 'common'
import WithTreeData from 'components/WithTreeData'
import { LinkEmbedView } from 'components/embeds/LinkEditor'
import Pantheon from 'components/embeds/Pantheon'
import { PassiveTreeWithSearch } from 'components/embeds/Passives'
import { SkillGemsEmbed } from 'components/embeds/SkillGems'
import { SkillGemsVideoDisplayEmbed } from 'components/embeds/SkillGemsVideoDisplay'
import { LevelingComponent } from 'leveling/leveling-component'

export function setCurrentSet(state: StoreState, set: string) {
  const setId = parseInt(set)
  if (state.embeds.length) {
    if (!isNaN(setId)) {
      const index = state.embeds.findIndex((embed) => embed.id === setId)
      if (index >= 0) {
        state = { ...state, activeEmbed: index }
      }
    }
  }
  return state
}

export function embedIndex(state: StoreState, id: number) {
  if (isNaN(id)) return -1
  return state.embeds.findIndex((embed) => embed.id === id)
}

export function PlannerPicker({
  type,
  profile,
  set,
  style
}: {
  type: string
  profile: string
  set: string
  style?: React.CSSProperties
}) {
  const planner = Store.useSelector((state) => state.planner)

  if (!planner) return <div className="poe-error">Unknown embed type!</div>

  switch (type) {
    case 'statPriority': {
      return <EquipmentStatPriority planner={planner} set={set} style={style} />
    }
    case 'skillRotation': {
      return <SkillRotationEmbed planner={planner} set={set} style={style} />
    }
    case 'equipment': {
      return (
        <PaperdollWithSets
          profileId={profile}
          planner={planner}
          sets={set.includes(',') ? set.split(',') : [set]}
          style={style}
        />
      )
    }
    case 'skills':
      return (
        <SkillGemsEmbed
          embed={{
            type: 'skills',
            id: 0,
            name: '',
            ...planner.skills
          }}
          style={style}
        />
      )
    case 'passives':
      return (
        <WithTreeData version={planner.version}>
          <PassiveTreeWithSearch
            embed={{
              type: 'passives',
              id: 0,
              name: '',
              charClass: planner.class,
              ascendancy: planner.ascendancy,
              version: planner.version,
              ...planner.passives
            }}
            preview={getTreePath(profile, 0)}
          />
        </WithTreeData>
      )
    case 'atlas':
      return (
        <WithTreeData version={planner.version}>
          <PassiveTreeWithSearch
            embed={{
              type: 'atlas',
              id: 1,
              name: '',
              version: planner.version,
              ...planner.atlas
            }}
            preview={getTreePath(profile, 1)}
          />
        </WithTreeData>
      )
  }
  return <div className="poe-error">Unknown embed type!</div>
}

export function EmbedPicker({
  type,
  profile,
  id,
  style
}: {
  type: string
  profile: string
  id: number
  style?: React.CSSProperties
}) {
  let embed = Store.useEmbed()
  if (!embed) return <div className="poe-error">Unknown embed type!</div>
  if (embed.type === 'skills' && type === 'skills-video-display') {
    embed = { ...embed, type }
  }
  if (type !== embed.type) return <div className="poe-error">Embed type mismatch!</div>
  switch (embed.type) {
    case 'equipment':
      return <Paperdoll items={embed.items} selected={undefined} onSelect={undefined} style={style} />
    case 'pantheon':
      return <Pantheon active={embed.active} onSelect={undefined} style={style} />
    case 'skills':
      return <SkillGemsEmbed embed={embed} style={style} />
    case 'skills-video-display':
      return <SkillGemsVideoDisplayEmbed embed={embed} style={style} />
    case 'link':
      return <LinkEmbedView embed={embed} />
    case 'ascendancy':
      return <AscendancyEmbedView embed={embed} style={style} />
    case 'leveling':
      return (
        <MainStoreProvider
          isMobile={window.innerWidth < 640}
          isDev={isDev}
          userHasSub={localStorage.getItem('maxrollUserHasSub') === 'true'}
          userLoaded={true}>
          <LevelingComponent embedId={embed.id} profileId={profile} />
        </MainStoreProvider>
      )
    case 'passives':
    case 'atlas':
      return (
        <WithTreeData version={embed.version}>
          <PassiveTreeWithSearch embed={embed} preview={getTreePath(profile, id)} />
        </WithTreeData>
      )
  }
  return <div className="poe-error">Unknown embed type!</div>
}
